// frontend/src/services/alignment/alignmentService.js

import api from '../api';
import cacheService from '../cache/cacheService';
import glossaryOptimizerService from '../glossary/glossaryOptimizerService';
import errorService from '../error/errorService';

/**
 * Alignment Service - Handles word and glossary term alignment between texts
 * with a modular post-processing approach
 */
class AlignmentService {
  constructor() {
    // A registry to track pending alignment requests by batch ID
    this.pendingAlignments = {};
  }

  /**
   * Compute word alignments between source and target texts (calls backend)
   * @param {string} sourceText - The source text
   * @param {string} targetText - The target text
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} - Promise that resolves with alignment data
   */
  async computeWordAlignments(sourceText, targetText, options = {}) { // Renamed from computeAlignments for clarity
    try {
      // Generate a cache key based on both texts
      const cacheKey = this._generateCacheKey(sourceText, targetText, 'word'); // Added type to cache key

      // Try to get alignments from cache first
      const cachedAlignments = cacheService.get(cacheKey);

      if (cachedAlignments) {
        console.log('Using cached word alignment data');
        return {
          ...cachedAlignments,
          source: 'cache'
        };
      }

      // Not in cache, compute alignments via API
      const response = await api.post('/align', { // This calls the Python ONNX word aligner
        source_text: sourceText,
        target_text: targetText
      });

      // Add type to identify this as word alignment
      const result = {
        ...response.data,
        type: 'word' // Ensure type is set
      };

      // Store result in cache
      if (result && !result.error) {
        cacheService.set(cacheKey, result, 3600); // Cache for 1 hour
      }

      return result;
    } catch (error) {
      // Return a standardized error response through the error service
      return errorService.handleApiError(error, 'Word alignment computation failed');
    }
  }

  /**
   * Compute glossary alignments between source and target texts (calls backend)
   * @param {string} sourceText - The source text
   * @param {string} targetText - The target text
   * @param {Array} glossary - Glossary terms
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} - Promise that resolves with glossary alignment data
   */
  async computeGlossaryAlignments(sourceText, targetText, glossary, sourceLanguage, targetLanguage, options = {}) {
    try {
      // Generate a cache key based on texts and glossary
      const cacheKey = this._generateCacheKey(sourceText, targetText, 'glossary', glossary);

      // Try to get alignments from cache first
      const cachedAlignments = cacheService.get(cacheKey);

      if (cachedAlignments) {
        console.log('Using cached glossary alignment data');
        return {
          ...cachedAlignments,
          source: 'cache'
        };
      }

      // Not in cache, request new alignment from backend
      const response = await api.post('/glossary-align', {
        source_text: sourceText,
        target_text: targetText,
        glossary: glossary,
        source_language: sourceLanguage, // Pass to backend
        target_language: targetLanguage  // Pass to backend
      });

      const result = {
        ...response.data,
        type: 'glossary'  // Ensure type is set from backend or set it here
      };

      // Store result in cache
      if (result && !result.error) {
        cacheService.set(cacheKey, result, 3600); // Cache for 1 hour
      }

      return result;
    } catch (error) {
      const errorResult = errorService.handleApiError(
        error,
        'Glossary alignment computation failed (API)'
      );
      return { ...errorResult, type: 'glossary' };
    }
  }


  /**
   * Request all configured alignment types for a batch
   * @param {string} batchId - The batch ID
   * @param {string} sourceText - Source text content
   * @param {string} targetText - Target text content
   * @param {Array} glossary - Optional glossary terms
   * @param {Function} onComplete - Callback for each alignment when complete: (batchId, processorType, result, isAllComplete) => {}
   * @returns {Object} - Request tracking information
   */
    async requestAlignments(batchId, sourceText, targetText, glossary = [], sourceLanguage = 'en', targetLanguage = 'en', onComplete = null) {
      const requestId = `align-${batchId}-${Date.now()}`;
      
      console.log(`alignmentService.requestAlignments called for batch ${batchId}`);
      console.log(`Source text length: ${sourceText ? sourceText.length : 0}, Target text length: ${targetText ? targetText.length : 0}`);
      console.log(`Glossary entries: ${glossary ? glossary.length : 0}`);


      let optimizedGlossary = [];
      if (glossary && glossary.length > 0 && sourceText) {
        try {
          optimizedGlossary = await glossaryOptimizerService.optimizeGlossary(
            sourceText,
            glossary, 
            sourceLanguage
          );
          console.log(`Glossary optimized for batch ${batchId}. Reduced from ${glossary.length} to ${optimizedGlossary.length} terms.`);
        } catch (error) {
          console.log(`Error optimizing glossary for batch ${batchId} alignments: ${error}. Proceeding without glossary alignment or with full if fallback needed.`);
          optimizedGlossary = glossary; // Fallback to full glossary if optimization errors
        }
      }

      // Define processorsToRun *after* glossary optimization
      const processorsToRun = ['word']; // Word alignment is always run
      if (optimizedGlossary.length > 0) {
        processorsToRun.push('glossary'); // Add glossary processor if there are terms
      }

      // Register this request
      this.pendingAlignments[requestId] = {
        batchId,
        processors: [...processorsToRun], // Now correctly includes 'glossary' if applicable
        completed: [],
        results: {},
        status: 'pending'
      };

      // Request word alignment (always runs)
      this._requestWordAlignment(requestId, sourceText, targetText, onComplete);

      // Request glossary alignment *if* it's in processorsToRun
      if (processorsToRun.includes('glossary')) {
        this._requestGlossaryAlignment(requestId, sourceText, targetText, optimizedGlossary, sourceLanguage, targetLanguage, onComplete);
      }
      // No 'else' block needed here to mark skipped. If 'glossary' is not in processorsToRun,
      // the alignment process will correctly complete once 'word' alignment is done.

      return {
        requestId,
        status: 'pending',
        processors: [...processorsToRun]
      };
    }

  /**
   * Cancel pending alignment requests for a batch
   * @param {string} batchId - The batch ID
   * @returns {boolean} - True if any requests were cancelled
   */
  cancelBatchAlignments(batchId) {
    let cancelled = false;

    // Find all pending requests for this batch
    Object.keys(this.pendingAlignments).forEach(requestId => {
      if (this.pendingAlignments[requestId] && this.pendingAlignments[requestId].batchId === batchId) {
        // Mark as cancelled
        this.pendingAlignments[requestId].status = 'cancelled';
        cancelled = true;
      }
    });

    // Clean up completed or cancelled items older than 5 minutes
    this._cleanupPendingAlignments();

    return cancelled;
  }

  /**
   * Check if a batch has pending alignments
   * @param {string} batchId - The batch ID
   * @returns {boolean} - True if the batch has pending alignments
   */
  hasPendingAlignments(batchId) {
    return Object.values(this.pendingAlignments).some(
      req => req && req.batchId === batchId && req.status === 'pending'
    );
  }

  /**
   * Get the status of alignments for a batch
   * @param {string} batchId - The batch ID
   * @returns {Object} - Status information
   */
  getAlignmentStatus(batchId) {
    // Find all requests for this batch
    const requests = Object.values(this.pendingAlignments)
      .filter(req => req && req.batchId === batchId);

    if (requests.length === 0) {
      return { status: 'none', pending: 0, completed: 0, total: 0 };
    }

    // Count pending and completed processors
    let pending = 0;
    let completed = 0;
    let total = 0;

    requests.forEach(req => {
      if (req.status === 'pending') {
        pending += req.processors.length - req.completed.length;
        completed += req.completed.length;
        total += req.processors.length;
      } else if (req.status === 'completed') {
        completed += req.processors.length;
        total += req.processors.length;
      }
    });

    return {
      status: pending > 0 ? 'pending' : 'completed',
      pending,
      completed,
      total
    };
  }

  /**
   * Private: Request word alignment (calls backend)
   */
  async _requestWordAlignment(requestId, sourceText, targetText, onComplete) {
    // Ensure the request still exists and isn't cancelled
    if (!this.pendingAlignments[requestId] ||
        this.pendingAlignments[requestId].status === 'cancelled') {
      return;
    }

    try {
      // Compute word alignments by calling the backend
      const alignments = await this.computeWordAlignments(sourceText, targetText);

      // Mark processor as complete
      this._markProcessorComplete(requestId, 'word', alignments, onComplete);
    } catch (error) { // This catch might be redundant if computeWordAlignments handles its own errors
      console.error('Error in _requestWordAlignment:', error);
      this._markProcessorComplete(requestId, 'word', {
        type: 'word',
        error: true,
        message: error.message || 'Failed to compute word alignments'
      }, onComplete);
    }
  }

  /**
   * Private: Request glossary term alignment (calls backend)
   */
  async _requestGlossaryAlignment(requestId, sourceText, targetText, glossary, sourceLanguage, targetLanguage, onComplete) {
    // Ensure the request still exists and isn't cancelled
    if (!this.pendingAlignments[requestId] ||
        this.pendingAlignments[requestId].status === 'cancelled') {
      return;
    }

    try {
      // Compute glossary alignments by calling the backend
      const glossaryAlignments = await this.computeGlossaryAlignments(sourceText, targetText, glossary, sourceLanguage, targetLanguage);

      // Mark processor as complete
      this._markProcessorComplete(requestId, 'glossary', glossaryAlignments, onComplete);
    } catch (error) { // This catch might be redundant
      console.error('Error in _requestGlossaryAlignment:', error);
      this._markProcessorComplete(requestId, 'glossary', {
        type: 'glossary',
        error: true,
        message: error.message || 'Failed to compute glossary alignments (API)'
      }, onComplete);
    }
  }


  /**
   * Private: Mark a processor as complete
   */
  _markProcessorComplete(requestId, processorType, result, onComplete) {
    // Ensure the request still exists
    if (!this.pendingAlignments[requestId]) {
      return;
    }

    const request = this.pendingAlignments[requestId];

    // Skip if already marked as complete or cancelled
    if (request.completed.includes(processorType) || request.status === 'cancelled') {
      return;
    }

    // Mark this processor as complete
    request.completed.push(processorType);
    request.results[processorType] = result;

    // Check if all processors are complete
    let allProcessorsCompleted = true;
    for (const proc of request.processors) {
        if (!request.completed.includes(proc)) {
            allProcessorsCompleted = false;
            break;
        }
    }

    if (allProcessorsCompleted) {
      request.status = 'completed';
    }

    // Invoke callback if provided
    if (onComplete && typeof onComplete === 'function') {
      const batchId = request.batchId;
      onComplete(batchId, processorType, result, request.status === 'completed');
    }

    // Clean up old entries periodically
    if (Math.random() < 0.1) { // 10% chance each time to avoid too frequent cleaning
      this._cleanupPendingAlignments();
    }
  }

  /**
   * Private: Clean up old/completed requests
   */
  _cleanupPendingAlignments() {
    const now = Date.now();
    const MAX_AGE = 5 * 60 * 1000; // 5 minutes

    Object.keys(this.pendingAlignments).forEach(requestId => {
      const request = this.pendingAlignments[requestId];
      if (!request) {
          delete this.pendingAlignments[requestId];
          return;
      }

      // Extract timestamp from request ID
      const match = requestId.match(/align-.*-(\d+)$/);
      if (match && match[1]) {
        const timestamp = parseInt(match[1], 10);

        // Clean up old or completed/cancelled requests
        if ((now - timestamp > MAX_AGE) ||
            request.status === 'completed' ||
            request.status === 'cancelled') {
          delete this.pendingAlignments[requestId];
        }
      } else { // If requestId format is unexpected, remove it to be safe
          delete this.pendingAlignments[requestId];
      }
    });
  }

  /**
   * Generate a cache key for alignment data
   * @private
   */
  _generateCacheKey(sourceText, targetText, type, glossary = null) {
    const textSample = (sourceText || '').substring(0, 100) + (targetText || '').substring(0, 100);
    let glossarySample = '';
    if (type === 'glossary' && glossary && glossary.length > 0) {
        glossarySample = glossary.slice(0,5).map(g => `${g.source || ''}:${g.target || ''}`).join('|');
    }
    // Include MODEL_ID in cache key for glossary-related items if it might change,
    // or generally for any embedding-dependent cache. For word alignment, it's fixed.
    // For glossary alignment, if its backend also uses a model, it should be part of its cache key implicitly.
    // Here, assuming MODEL_ID primarily affects client-side glossary optimization which is not directly cached by this service,
    // but if this service were to cache results *after* client-side optimization, it would matter.
    // For now, let's make it specific for glossary type if client-side models are involved in creating the `glossary` input.
    const inputGlossaryTypeIdentifier = type === 'glossary' ? (glossary && glossary.length > 0 ? 'optimized_glossary_input' : 'no_glossary_input') : 'word_alignment_input';
    const fullSample = `${type}|${textSample}|${glossarySample}|${inputGlossaryTypeIdentifier}`;

    let hash = 0;
    for (let i = 0; i < fullSample.length; i++) {
      hash = (hash << 5) - hash + fullSample.charCodeAt(i);
      hash |= 0; // Convert to 32bit integer
    }
    return `alignment_${hash.toString(16)}`;
  }
}

export default new AlignmentService();